<script setup>
import { ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { message } from 'ant-design-vue';
import { showErrorMessage } from '@/utils/messageHelper';
import {
    LockOutlined,
    UserOutlined,
    LoadingOutlined
} from '@ant-design/icons-vue';
import { useAdminInfo } from '@/stores/useAdminInfo';

const adminStore = useAdminInfo();
const router = useRouter();
const route = useRoute();
const email = ref('');
const password = ref('');
const emailError = ref(false);
const passwordError = ref(false);
const isLoading = ref(false);
const emailErrorMessage = ref('');
const rememberMe = ref(false);

const resolvePostLoginPath = () => {
    const raw =
        typeof route.query.next === 'string'
            ? route.query.next
            : typeof route.query.redirect === 'string'
              ? route.query.redirect
              : '';
    if (
        raw.startsWith('/') &&
        !raw.startsWith('//') &&
        !raw.includes('#')
    ) {
        return raw;
    }
    return '';
};

const dashboardHref = () => {
    const base = import.meta.env.BASE_URL || '/';
    return `${base}dashboard`.replace(/\/{2,}/g, '/');
};

const goAfterLogin = async () => {
    const nextPath = resolvePostLoginPath();
    try {
        if (nextPath) {
            await router.replace(nextPath);
        } else {
            await router.replace({ name: 'Dashboard' });
        }
        const current = router.currentRoute.value;
        if (current.name === 'Login' || current.path === '/login') {
            window.location.assign(nextPath || dashboardHref());
        }
    } catch (navError) {
        console.error('Login redirect failed:', navError);
        window.location.assign(nextPath || dashboardHref());
    }
};

const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

const validateInputs = () => {
    const emailTrimmed = email.value.trim();
    const passwordTrimmed = password.value.trim();

    // 驗證 email 格式
    if (emailTrimmed === '') {
        emailError.value = true;
        emailErrorMessage.value = 'Email 不能為空';
    } else if (!validateEmail(emailTrimmed)) {
        emailError.value = true;
        emailErrorMessage.value = '請輸入有效的 Email 格式';
    } else {
        emailError.value = false;
        emailErrorMessage.value = '';
    }

    passwordError.value = passwordTrimmed === '';

    return !emailError.value && !passwordError.value;
};

const handleLogin = async () => {
    if (!validateInputs()) {
        return;
    }
    isLoading.value = true;

    try {
        const result = await adminStore.login(email.value, password.value);

        if (!result.success) {
            showErrorMessage(result.message || '登入失敗');
            return;
        }

        message.success('登入成功');
        const currentTime = new Date().toLocaleString();
        localStorage.setItem('lastLoginTime', currentTime);

        if (rememberMe.value) {
            localStorage.setItem('rememberedEmail', email.value);
        } else {
            localStorage.removeItem('rememberedEmail');
        }

        await goAfterLogin();
    } finally {
        isLoading.value = false;
    }
};

const handleClear = () => {
    email.value = '';
};

const handleEmailChange = (e) => {
    email.value = e.target.value;
    const emailTrimmed = email.value.trim();

    if (emailTrimmed === '') {
        emailError.value = true;
        emailErrorMessage.value = 'Email 不能為空';
    } else if (!validateEmail(emailTrimmed)) {
        emailError.value = true;
        emailErrorMessage.value = '請輸入有效的 Email 格式';
    } else {
        emailError.value = false;
        emailErrorMessage.value = '';
    }
};

const handlePasswordChange = (e) => {
    password.value = e.target.value;
    if (e.target.value.trim() === '') {
        passwordError.value = true;
    } else {
        passwordError.value = false;
    }
};

// 頁面載入時，從 localStorage 讀取儲存的帳號
onMounted(() => {
    const rememberedEmail = localStorage.getItem('rememberedEmail');
    if (rememberedEmail) {
        email.value = rememberedEmail;
        rememberMe.value = true;
    }
});
</script>

<template>
    <div class="login-page">
        <div class="login-page__bg" aria-hidden="true">
            <div class="login-page__panel"></div>
            <div class="login-page__glow"></div>
        </div>

        <div class="login-card-wrap">
            <div class="login-card">
                <div class="card-header">
                    <img
                        src="/worldtree-core-logo.png"
                        alt="世界樹核心 logo"
                        class="login-brand-logo"
                    />
                    <h1 class="card-title">世界樹核心</h1>
                    <p class="header-subtitle">管理員後台</p>
                </div>

                <div class="form-container">
                    <div class="input-group">
                        <div
                            class="input-wrapper"
                            :class="{ error: emailError }"
                        >
                            <span class="input-icon"><UserOutlined /></span>
                            <input
                                v-model="email"
                                type="email"
                                placeholder="請輸入 Email"
                                class="login-input"
                                autocomplete="username"
                                @input="handleEmailChange"
                                @keyup.enter="handleLogin"
                            />
                        </div>
                        <div v-if="emailError" class="error-message">
                            {{ emailErrorMessage }}
                        </div>
                    </div>

                    <div class="input-group">
                        <div
                            class="input-wrapper"
                            :class="{ error: passwordError }"
                        >
                            <span class="input-icon"><LockOutlined /></span>
                            <input
                                v-model="password"
                                type="password"
                                placeholder="請輸入密碼"
                                class="login-input"
                                autocomplete="current-password"
                                @input="handlePasswordChange"
                                @keyup.enter="handleLogin"
                            />
                        </div>
                        <div v-if="passwordError" class="error-message">
                            密碼不能為空！
                        </div>
                    </div>

                    <label class="remember-me">
                        <input v-model="rememberMe" type="checkbox" />
                        <span>記住我</span>
                    </label>

                    <button
                        class="login-btn"
                        type="button"
                        :disabled="isLoading"
                        @click="handleLogin"
                    >
                        <LoadingOutlined v-if="isLoading" />
                        <span v-else>登入系統</span>
                    </button>
                </div>

                <p class="copyright">© 2025 GongHao</p>
            </div>
        </div>
    </div>
</template>

<style scoped>
.login-page {
    min-height: 100vh;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    background: #e8ecf1;
    font-family: var(--font-ui, 'Noto Sans TC', system-ui, sans-serif);
}

.login-page__bg {
    position: absolute;
    inset: 0;
    pointer-events: none;
}

.login-page__panel {
    position: absolute;
    inset: 0;
    background:
        radial-gradient(
            1200px 600px at 12% -10%,
            rgba(42, 74, 122, 0.12),
            transparent 60%
        ),
        radial-gradient(
            900px 500px at 100% 100%,
            rgba(14, 116, 144, 0.08),
            transparent 55%
        ),
        linear-gradient(160deg, #f4f6f9 0%, #e8ecf1 48%, #dde3ea 100%);
}

.login-page__glow {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    height: 4px;
    background: linear-gradient(
        90deg,
        transparent,
        #2a4a7a 35%,
        #0e7490 65%,
        transparent
    );
    opacity: 0.85;
}

.login-card-wrap {
    position: relative;
    z-index: 2;
    width: min(420px, calc(100vw - 32px));
    padding: 24px 0;
}

.login-card {
    background: #fff;
    border: 1px solid rgba(28, 34, 40, 0.1);
    border-radius: 14px;
    padding: 36px 32px 28px;
    box-shadow:
        0 1px 2px rgba(28, 34, 40, 0.04),
        0 18px 40px rgba(28, 34, 40, 0.08);
}

.card-header {
    text-align: center;
    margin-bottom: 28px;
}

.login-brand-logo {
    width: 56px;
    height: 56px;
    border-radius: 12px;
    object-fit: cover;
    box-shadow: 0 6px 16px rgba(42, 74, 122, 0.2);
}

.card-title {
    margin: 14px 0 4px;
    font-size: 22px;
    font-weight: 700;
    color: #1c2228;
    letter-spacing: 0.04em;
}

.header-subtitle {
    margin: 0;
    font-size: 13px;
    color: #5c6570;
    font-weight: 500;
}

.form-container {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.input-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.input-wrapper {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0 14px;
    height: 44px;
    border: 1px solid rgba(28, 34, 40, 0.14);
    border-radius: 8px;
    background: #f7f9fb;
    transition:
        border-color 0.15s ease,
        box-shadow 0.15s ease,
        background 0.15s ease;
}

.input-wrapper:focus-within {
    background: #fff;
    border-color: #2a4a7a;
    box-shadow: 0 0 0 3px rgba(42, 74, 122, 0.14);
}

.input-wrapper.error {
    border-color: #b42318;
    box-shadow: 0 0 0 3px rgba(180, 35, 24, 0.12);
}

.input-icon {
    color: #5c6570;
    display: inline-flex;
    font-size: 15px;
}

.login-input {
    flex: 1;
    border: none;
    outline: none;
    background: transparent;
    font-size: 14px;
    color: #1c2228;
    font-family: inherit;
}

.login-input::placeholder {
    color: #8b949e;
}

.error-message {
    font-size: 12px;
    color: #b42318;
    padding-left: 2px;
}

.remember-me {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #3a4450;
    user-select: none;
    cursor: pointer;
}

.remember-me input {
    width: 15px;
    height: 15px;
    accent-color: #2a4a7a;
}

.login-btn {
    margin-top: 6px;
    height: 44px;
    border: none;
    border-radius: 8px;
    background: #2a4a7a;
    color: #fff;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s ease;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-family: inherit;
}

.login-btn:hover:not(:disabled) {
    background: #1e3a5f;
}

.login-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.copyright {
    margin: 22px 0 0;
    text-align: center;
    font-size: 12px;
    color: #8b949e;
}

@media (max-width: 480px) {
    .login-card {
        padding: 28px 20px 22px;
    }
}
</style>
